import type { ReactElement } from "react";
import React from "react";
import { RawMoney } from "@edreamsodigeo/prisma-design-system";

import type { SubscriptionOfferDuration } from "~/generated/graphql";
import {
  SubscriptionOfferPeriod,
  SubscriptionOfferTimeUnit,
} from "~/generated/graphql";
import { isNullOrUndefined } from "../utils";
import { RENEWAL_DURATION_KEYS } from "./config/constants";
import type { RenewalPriceProps } from "./contract";

/**
 * Constants
 */
const NUMBER_DAYS_OF_MONTH = 30;
export const DEFAULT_RENEWAL_DURATION: SubscriptionOfferDuration = {
  amount: 12,
  timeUnit: SubscriptionOfferTimeUnit.Months,
};

export const QUARTERLY_RENEWAL_DURATION: SubscriptionOfferDuration = {
  amount: 3,
  timeUnit: SubscriptionOfferTimeUnit.Months,
};

export const isYearlyDurationSubscription = (
  renewalDuration: SubscriptionOfferDuration | undefined,
): boolean | null => {
  if (!renewalDuration) {
    return null;
  }

  return (
    renewalDuration.timeUnit === SubscriptionOfferTimeUnit?.Months &&
    renewalDuration.amount === 12
  );
};

export const convertOfferDurationToDays = (
  offerDuration: SubscriptionOfferDuration | undefined,
): number | null => {
  if (!offerDuration) {
    return null;
  }

  let numberOfDays: number = offerDuration.amount;

  if (offerDuration.timeUnit === SubscriptionOfferTimeUnit?.Months) {
    numberOfDays *= NUMBER_DAYS_OF_MONTH;
  }

  return numberOfDays;
};

export const RenewalPrice = ({
  currency,
  amount,
}: RenewalPriceProps): ReactElement => {
  if (!currency || !amount) {
    return <></>;
  }

  return <RawMoney currency={currency}>{amount}</RawMoney>;
};

export const durationFn = (
  renewalDuration: SubscriptionOfferDuration | undefined,
): string => {
  if (!renewalDuration) return "";
  const { amount, timeUnit } = renewalDuration;
  const duration = timeUnit.toLowerCase();
  if (timeUnit === SubscriptionOfferTimeUnit.Months) {
    if (amount === 1) return "month";
    else if (amount === 3) return "quarter";
    else if (amount === 12) return "year";
  }
  return duration;
};

const getRenewalTimeKeyByMonthsTimeUnit = (
  amount: number,
  isAdjective: boolean = false,
): string => {
  const monthKeyMap: Record<number, { adjective: string; singular: string }> = {
    1: {
      adjective: RENEWAL_DURATION_KEYS.adjective.month,
      singular: RENEWAL_DURATION_KEYS.singular.month,
    },
    3: {
      adjective: RENEWAL_DURATION_KEYS.adjective.quarter,
      singular: RENEWAL_DURATION_KEYS.singular.quarter,
    },
    12: {
      adjective: RENEWAL_DURATION_KEYS.adjective.year,
      singular: RENEWAL_DURATION_KEYS.singular.year,
    },
  };

  const match = monthKeyMap[amount];
  if (match) {
    return isAdjective ? match.adjective : match.singular;
  }

  return RENEWAL_DURATION_KEYS.plural.months;
};

export const renewalTimeKey = (
  renewalDuration: SubscriptionOfferDuration | undefined,
  isAdjective: boolean = false,
): string => {
  if (!renewalDuration) return "";

  const { amount, timeUnit } = renewalDuration;

  switch (timeUnit) {
    case SubscriptionOfferTimeUnit.Days:
      return RENEWAL_DURATION_KEYS.plural.days;
    case SubscriptionOfferTimeUnit.Months:
      return getRenewalTimeKeyByMonthsTimeUnit(amount, isAdjective);
    default:
      return "";
  }
};

export const freeTrialDays = (
  offerDuration: SubscriptionOfferDuration | undefined,
): number => {
  const offerDurationInDays = convertOfferDurationToDays(offerDuration);
  if (isNullOrUndefined(offerDurationInDays)) {
    return 0;
  }

  return offerDurationInDays;
};

export const isAnyOfferPaid = (offer): boolean =>
  offer?.period === SubscriptionOfferPeriod.Paid ||
  offer?.subscriptionPeriod === SubscriptionOfferPeriod.Paid;

export const isMonthlySubscription = (
  renewalDuration: SubscriptionOfferDuration | undefined,
): boolean => {
  const timeUnit = renewalDuration?.timeUnit ?? "Months";
  const amount = renewalDuration?.amount ?? 12;

  return timeUnit === SubscriptionOfferTimeUnit.Months && amount === 1;
};
